import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { financialsApi } from '@/api/financials'
import { ApiError, showErrorToast, showSuccessToast } from '@/api/errorHandler'
import { useLocalization } from '@/contexts/LocalizationContext'
import { useOrganization } from '@/contexts/OrganizationContext'
import { PAYOUT_DOCUMENT_ACCEPT, PAYOUT_DOCUMENT_MAX_SIZE_MB, PAYOUT_TEXT_MAX_LENGTH } from '@/lib/payoutDocument'
import type { PayoutStatement } from '@/types/financials'

type Translate = (key: string, params?: Record<string, string | number>) => string

/**
 * Turn an approve/dispute failure into a localized message.
 *
 * The document endpoints answer with a `code` plus, for two of them, extra
 * context: `allowed_types` on 415 and `max_size_mb` on 413. A 413 raised by the
 * ingress proxy instead of Django carries neither a `code` nor those fields, so
 * every lookup here has to tolerate them being absent.
 */
function localizePayoutActionError(error: unknown, t: Translate): string | null {
  if (!(error instanceof ApiError)) return null

  const payload = (error.payload && typeof error.payload === 'object' ? error.payload : {}) as {
    allowed_types?: unknown
    max_size_mb?: unknown
  }

  const allowedTypes = Array.isArray(payload.allowed_types)
    ? payload.allowed_types.join(', ')
    : PAYOUT_DOCUMENT_ACCEPT
  const maxSizeMb =
    typeof payload.max_size_mb === 'number' ? payload.max_size_mb : PAYOUT_DOCUMENT_MAX_SIZE_MB

  switch (error.code) {
    case 'document_required':
      return t('financials.document_required')
    case 'document_empty':
      return t('financials.document_empty')
    case 'document_unsupported_type':
    case 'UNSUPPORTED_MEDIA_TYPE':
      return t('financials.document_unsupported_type', { types: allowedTypes })
    case 'document_too_large':
    case 'PAYLOAD_TOO_LARGE':
      return t('financials.document_too_large', { max: maxSizeMb })
    case 'notes_too_long':
      return t('financials.notesMaxLength', { max: PAYOUT_TEXT_MAX_LENGTH })
    case 'reason_too_long':
      return t('financials.reasonMaxLength', { max: PAYOUT_TEXT_MAX_LENGTH })
    default:
      // Guards (404 / 403 / wrong-status 400) still carry a usable `error` string.
      return null
  }
}

export type PayoutSectionLabel = 'Pending Approval' | 'In Progress' | 'Processed' | 'Disapproved'

export interface PayoutSections {
  pendingApproval: PayoutStatement[]
  inProgress: PayoutStatement[]
  processed: PayoutStatement[]
  disapproved: PayoutStatement[]
}

/**
 * Get the total amount for a payout statement.
 * Supports both new backend format (grossAmount/netAmount) and legacy format.
 */
function getTotal(statement: PayoutStatement): number {
  // Prefer grossAmount from new backend
  if (typeof statement.grossAmount === 'number') return statement.grossAmount
  // Fallback to totalAmount
  if (typeof statement.totalAmount === 'number') return statement.totalAmount
  // Legacy calculation
  return (statement.totalProviderCut || 0) + (statement.totalProviderVat || 0)
}

function normalizeStatus(status: string | null | undefined): string {
  return (status || '').toLowerCase().replace(/-/g, '_')
}

/**
 * Map backend status to UI section label.
 * 
 * Pending Approval: pending_review
 * In Progress: approved, confirmed_by_wtd, confirmed_by_provider, invoiced, processing
 * Processed: paid
 * Disapproved: cancelled, disputed
 * 
 * Note: draft statements are excluded (not shown in any tab).
 */
export function getSectionLabelForStatus(status: string | null | undefined): PayoutSectionLabel | null {
  const s = normalizeStatus(status)
  
  // Exclude draft — not ready for provider view
  if (s === 'draft') return null

  // Pending Approval: pending_review
  if (s === 'pending_review') return 'Pending Approval'
  
  // Disapproved: cancelled, disputed
  if (s === 'cancelled' || s === 'disputed') return 'Disapproved'
  
  // Processed: paid
  if (s === 'paid') return 'Processed'
  
  // In Progress: approved, confirmed_by_wtd, confirmed_by_provider, invoiced, processing
  if (
    s === 'approved' || 
    s === 'confirmed_by_wtd' || 
    s === 'confirmed_by_provider' || 
    s === 'invoiced' || 
    s === 'processing' ||
    s === 'in_progress'
  ) {
    return 'In Progress'
  }
  
  return null
}

export function usePayoutStatements() {
  const queryClient = useQueryClient()
  const { t } = useLocalization()
  const { currentOrganization } = useOrganization()

  const query = useQuery({
    queryKey: ['payoutStatements', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) return []
      // New backend endpoint filters by provider profile UUID (provider_id).
      // We don't have ProfileProvider.id here, so fetch without provider_id.
      //
      // Every page is fetched because the tab counts and KPI totals below must
      // cover the whole data set, and the backend `status` filter accepts only
      // one status (tabs such as "In Progress" span several).
      const statements = await financialsApi.listAllPayoutStatements()

      // Sort newest-first (by periodStart if present)
      return [...statements].sort((a, b) => {
        const ad = a.periodStart ? new Date(a.periodStart).getTime() : 0
        const bd = b.periodStart ? new Date(b.periodStart).getTime() : 0
        return bd - ad
      })
    },
    enabled: !!currentOrganization?.id,
    staleTime: 1000 * 30,
  })

  const statements = query.data || []

  const sections: PayoutSections = {
    pendingApproval: [],
    inProgress: [],
    processed: [],
    disapproved: [],
  }

  for (const s of statements) {
    const label = getSectionLabelForStatus(s.status)
    if (!label) continue
    if (label === 'Pending Approval') sections.pendingApproval.push(s)
    else if (label === 'In Progress') sections.inProgress.push(s)
    else if (label === 'Processed') sections.processed.push(s)
    else sections.disapproved.push(s)
  }

  const summary = {
    pendingApprovalCount: sections.pendingApproval.length,
    inProgressCount: sections.inProgress.length,
    processedCount: sections.processed.length,
    disapprovedCount: sections.disapproved.length,
    pendingApprovalAmount: sections.pendingApproval.reduce((sum, s) => sum + getTotal(s), 0),
    inProgressAmount: sections.inProgress.reduce((sum, s) => sum + getTotal(s), 0),
    processedAmount: sections.processed.reduce((sum, s) => sum + getTotal(s), 0),
    disapprovedAmount: sections.disapproved.reduce((sum, s) => sum + getTotal(s), 0),
  }

  const invalidateStatements = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['payoutStatements'] }),
      // The detail cache holds the signed document URL — refetch for a fresh one.
      queryClient.invalidateQueries({ queryKey: ['payoutStatementDetails'] }),
    ])
  }

  const handleActionError = (e: unknown) => {
    const message = localizePayoutActionError(e, t)
    if (message) {
      showErrorToast(message)
      return
    }
    showErrorToast(e as Error)
  }

  const approveMutation = useMutation({
    mutationFn: async (input: { statementId: string; document: File; notes?: string }) => {
      await financialsApi.approvePayoutStatement(input)
    },
    onSuccess: async () => {
      showSuccessToast(t('financials.approveSuccess'))
      await invalidateStatements()
    },
    onError: handleActionError,
  })

  const disputeMutation = useMutation({
    mutationFn: async (input: { statementId: string; reason: string; document: File }) => {
      await financialsApi.disputePayoutStatement(input)
    },
    onSuccess: async () => {
      showSuccessToast(t('financials.disputeSuccess'))
      await invalidateStatements()
    },
    onError: handleActionError,
  })

  return {
    statements,
    sections,
    summary,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,

    approve: approveMutation.mutateAsync,
    dispute: disputeMutation.mutateAsync,
    isApproving: approveMutation.isPending,
    isDisputing: disputeMutation.isPending,
  }
}
